import type { ComponentPropsWithoutRef } from 'react';
import { Message } from '@gonfalon/messages/Message';
import { msg } from '@gonfalon/messages/msg';
import {
  Button,
  FieldError,
  Input,
  Label,
  ListBox,
  ListBoxItem,
  Popover,
  Select,
  SelectValue,
  TextField,
} from '@launchpad-ui/components';
import { Icon } from '@launchpad-ui/icons';
import clsx from 'clsx';

import type { WindowUnit } from '../../../CreateMetricModal/formUtils';
import { WINDOW_UNITS, windowUnitLabel } from '../windowingUtils';

import styles from './WindowFieldsRow.module.css';

export interface WindowFieldsRowProps {
  windowUnit: WindowUnit;
  windowStart: number | undefined;
  windowEnd: number | undefined;
  showStartInvalid: boolean;
  showEndInvalid: boolean;
  startError?: string;
  endError?: string;
  unitLabel: WindowUnit;
  maxInUnit: number;
  onUnitChange: (unit: WindowUnit) => void;
  /** Props spread directly onto the Start <Input> (from react-hook-form register) */
  startInputProps: ComponentPropsWithoutRef<'input'>;
  /** Props spread directly onto the End <Input> (from react-hook-form register) */
  endInputProps: ComponentPropsWithoutRef<'input'>;
  /** `section` widens the unit select and takes the smaller type scale a titled section uses. */
  variant?: 'inline' | 'section';
  className?: string;
}

export const WindowFieldsRow: React.FC<WindowFieldsRowProps> = ({
  windowUnit,
  windowStart,
  windowEnd,
  showStartInvalid,
  showEndInvalid,
  startError,
  endError,
  unitLabel,
  maxInUnit,
  onUnitChange,
  startInputProps,
  endInputProps,
  variant = 'inline',
  className,
}) => (
  <div className={clsx(styles.row, variant === 'section' && styles.rowInSection, className)}>
    <Select
      value={windowUnit}
      onChange={(key) => onUnitChange(key as WindowUnit)}
      aria-label={msg('metrics.measurementWindow.unitLabel')}
      className={styles.unitSelect}
    >
      <Label>
        <Message id="metrics.measurementWindow.unitLabel" />
      </Label>
      <Button>
        <SelectValue />
        <Icon name="chevron-down" size="small" />
      </Button>
      <Popover>
        <ListBox>
          {WINDOW_UNITS.map((unit) => (
            <ListBoxItem key={unit.id} id={unit.id}>
              {windowUnitLabel(unit.id)}
            </ListBoxItem>
          ))}
        </ListBox>
      </Popover>
    </Select>

    <TextField isInvalid={showStartInvalid} className={styles.offsetInput}>
      <Label>
        <Message id="metrics.measurementWindow.start" />
      </Label>
      <Input
        {...startInputProps}
        type="number"
        value={windowStart !== undefined && !Number.isNaN(windowStart) ? windowStart : ''}
        min={0}
        max={maxInUnit}
        step={1}
      />
      {showStartInvalid ? (
        <FieldError className={styles.fieldError}>{startError}</FieldError>
      ) : (
        <span className={styles.inputHint}>{msg('metrics.measurementWindow.afterExposure', { unit: unitLabel })}</span>
      )}
    </TextField>

    <TextField isInvalid={showEndInvalid} className={styles.offsetInput}>
      <Label>
        <Message id="metrics.measurementWindow.end" />
      </Label>
      <Input
        {...endInputProps}
        type="number"
        value={windowEnd !== undefined && !Number.isNaN(windowEnd) ? windowEnd : ''}
        min={1}
        max={maxInUnit}
        step={1}
      />
      {showEndInvalid ? (
        <FieldError className={styles.fieldError}>{endError}</FieldError>
      ) : (
        <span className={styles.inputHint}>{msg('metrics.measurementWindow.afterExposure', { unit: unitLabel })}</span>
      )}
    </TextField>
  </div>
);
